import { createFileRoute } from "@tanstack/react-router";
import SeoWebCiudad, { citiesData, cityGeo, cityProfiles } from "@/pages/servicios/SeoWebCiudad";
import { buildBreadcrumb, ldJson } from "@/lib/site-schema";

export const Route = createFileRoute("/servicios/seo-web/$ciudad")({
  component: SeoWebCiudad,
  head: ({ params }) => {
    const ciudadLower = params.ciudad?.toLowerCase();
    const cityData = ciudadLower ? citiesData[ciudadLower] : null;

    if (!cityData) {
      return {
        meta: [
          { title: "Ciudad no disponible | SEOEVOLUTION" },
          { name: "robots", content: "noindex" },
        ],
      };
    }

    const title = `Agencia SEO Web en ${cityData.name} | SEOEVOLUTION`;
    const description = `Agencia SEO Web premium en ${cityData.name}. Ejecutamos el trabajo profesional para que las webs de empresas de ${cityData.province} compitan por las primeras posiciones de Google.`;
    const canonicalUrl = `https://agenciaseoevolution.com/servicios/seo-web/${ciudadLower}`;
    const geo = ciudadLower ? cityGeo[ciudadLower] : undefined;
    const profile = ciudadLower ? cityProfiles[ciudadLower] : undefined;

    const businessSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${canonicalUrl}#business`,
      "name": `SEOEVOLUTION — Agencia SEO Web en ${cityData.name}`,
      "description": `Agencia SEO Web premium en ${cityData.name}. Trabajo profesional para competir por las primeras posiciones de Google.`,
      "url": canonicalUrl,
      "telephone": "+34910626684",
      "email": "contacto@agenciaseoevolution.com",
      "areaServed": {
        "@type": "City",
        "name": cityData.name,
        "containedIn": { "@type": "AdministrativeArea", "name": cityData.province },
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": cityData.name,
        "addressRegion": cityData.province,
        "postalCode": geo?.postalCode,
        "addressCountry": "ES",
      },
      ...(geo && {
        geo: { "@type": "GeoCoordinates", latitude: geo.lat, longitude: geo.lng },
      }),
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
          "opens": "10:00",
          "closes": "17:00",
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Friday"],
          "opens": "10:00",
          "closes": "14:00",
        },
      ],
    };

    const faqSchema = profile && {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": profile.faqs.map((f) => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": { "@type": "Answer", "text": f.answer },
      })),
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: cityData.keywords.join(', ') },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
      ],
      scripts: [
        ldJson(
          buildBreadcrumb([
            { name: "SEO Web con IA", path: "/servicios/seo-web-ia" },
            { name: cityData.name, path: `/servicios/seo-web/${ciudadLower}` },
          ]),
        ),
        { type: "application/ld+json", children: JSON.stringify(businessSchema) },
        ...(faqSchema ? [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }] : []),
      ],
    };
  },
});
